import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { invalidateChangeRequestFeeds } from '../invalidateChangeRequestFeeds';

export interface OverrideChangeRequestInput {
  changeRequestId: string;
  /** Rationale recorded as the CR Comment. */
  comment: string | null;
  /** `true` → force-approve (+merge); `false` → force-reject. */
  // no-dd-sa:typescript-best-practices/boolean-prop-naming -- tRPC mutation-input DTO field mirroring the server `override` contract, not a React prop.
  approved: boolean;
}

export interface OverrideChangeRequestLevelInput {
  changeRequestId: string;
  /** The approval level to override. */
  levelId: string;
  comment: string | null;
  /** `true` → approve the level; `false` → reject. */
  // no-dd-sa:typescript-best-practices/boolean-prop-naming -- tRPC mutation-input DTO field mirroring the server `overrideLevel` contract, not a React prop.
  approved: boolean;
}

/**
 * tRPC implementation of the CR override (RSP-1528 §11). Feature/consumer code
 * must NOT call this directly — use the flag-gated `useOverrideChangeRequest`
 * wrapper. The folder barrel (`index.ts`) re-exports this hook (matching the
 * repo's mutation-wrapper convention) only so that wrapper can import it; the
 * re-export is not an invitation for other call sites. Authorisation (the CR
 * parent entity's Manager role) is enforced server-side; a 403 rejects
 * `mutateAsync`, which the modal surfaces.
 */
export const useOverrideChangeRequestTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  // Mirror the Apollo path's `evictField(cache, 'change_request_by_pk')` —
  // refresh every CR read surface after either override (the all-statuses
  // by-parent feed is what the panel renders from; without it the override's
  // status flip stays invisible until the next poll tick).
  const invalidate = async () =>
    invalidateChangeRequestFeeds(queryClient, trpc);

  const mutation = useMutation({
    ...trpc.frontend.changeRequest.override.mutationOptions({
      onSuccess: invalidate,
    }),
  });

  const levelMutation = useMutation({
    ...trpc.frontend.changeRequest.overrideLevel.mutationOptions({
      onSuccess: invalidate,
    }),
  });

  return {
    overrideChangeRequest: async (input: OverrideChangeRequestInput) => {
      await mutation.mutateAsync(input);
    },
    overrideChangeRequestLevel: async (
      input: OverrideChangeRequestLevelInput
    ) => {
      await levelMutation.mutateAsync(input);
    },
    loading: mutation.isPending || levelMutation.isPending,
    error: mutation.error ?? levelMutation.error,
  };
};
